import { yupResolver } from "@hookform/resolvers/yup";
import { uniq } from "lodash";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { type DeepPartial } from "react-hook-form";
import styled from "styled-components";

import { useGetAggregatedEntityIds } from "@ds/hooks/use-get-aggregated-entity-ids";
import { useDeepRootSelector, useRootDispatch, useRootSelector } from "@ds/hooks";

import { isAggregatedEntity } from "@ds/model/entity-model";

import { getPlayerStatusIndicator } from "@ds/modules/devices/players/components/common/status-indicator";
import { selectPlayersByIds, selectTablePlayers } from "@ds/modules/devices/players/redux/selectors";
import { playersActions } from "@ds/modules/devices/players/redux/slice";
import { DetailsTableDataTypeEnum } from "@ds/modules/table-data/utils/model";

import {
  DetailsCard,
  DetailsCardRow,
  DetailsCardRowWrapper,
  EditableDetailsCardDecorator,
  EditableDetailsCardRow,
  EditableDetailsCardRowDecorator,
} from "@ds/components/details-view";
import { MultipleValuesLabelWithLink, UnassignedLabel } from "@ds/components/forms/labels/common";
import { type DataTableRowReorderEvent } from "@ds/components/table/data-table";
import { DetailsTable } from "@ds/components/table/details-table";
import { Column, getReorderColumn } from "@ds/components/table/table-columns";
import { TextLink } from "@ds/components/text-link";
import { isMultipleFieldValuesInAggregatedEntity } from "@ds/utils/entities";
import { composeDetailsRouteByEntityType } from "@ds/utils/router";

import { experiencesActions } from "../../redux/slice";
import { selectExperiencesByIds } from "../../redux/selectors";
import { updateValidationSchema } from "../../utils/validation-schema";
import { ExperiencePlayersControl } from "../forms/controls/experience-players-control";
import { ExperienceDetailsPlayersCardActionsMenu } from "./experience-details-players-card-actions-menu";

export const Wrapper = styled(DetailsCardRowWrapper)`
  :last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

export const ExperienceDetailsPlayersCard: FC<{
  experience: Experience;
  onSubmit: (changedFields: DeepPartial<Experience>) => void;
}> = ({ experience, onSubmit }) => {
  const dispatch = useRootDispatch();

  const aggregatedExperienceIds = useGetAggregatedEntityIds(experience);
  const aggregatedExperiencePlayers = useDeepRootSelector(state => {
    const aggregatedExperiences = selectExperiencesByIds(state, aggregatedExperienceIds);
    const aggregatedPlayersIds = uniq(aggregatedExperiences.flatMap(({ device_ids }) => device_ids).filter(_ => _));
    return selectPlayersByIds(state, aggregatedPlayersIds, "experience_order");
  });

  const tablePlayers = useRootSelector(state => selectTablePlayers(state, DetailsTableDataTypeEnum.ExperiencePlayers));

  // Local state for optimistic reordering
  const [localPlayers, setLocalPlayers] = useState<Player[]>([]);

  // Initialize local state when aggregatedExperiencePlayers changes
  useEffect(() => {
    setLocalPlayers(aggregatedExperiencePlayers);
  }, [aggregatedExperiencePlayers]);

  // Sync local state with Redux table state
  useEffect(() => {
    if (localPlayers.length) {
      dispatch(
        playersActions.setTablePlayers(localPlayers, {
          tableType: DetailsTableDataTypeEnum.ExperiencePlayers,
        }),
      );
    }
  }, [dispatch, localPlayers]);

  // Detect if user has reordered players (dirty state)
  const isReordering = useMemo(() => {
    if (localPlayers.length !== aggregatedExperiencePlayers.length) return false;

    // Compare order by checking if player IDs match in sequence
    return !localPlayers.every((player, index) => player.id === aggregatedExperiencePlayers[index]?.id);
  }, [localPlayers, aggregatedExperiencePlayers]);

  const onReorderHandler = useCallback((currIndex: number, newIndex: number) => {
    if (currIndex !== newIndex) {
      // Update local state only
      setLocalPlayers(prevPlayers => {
        const reordered = [...prevPlayers];
        const [movedPlayer] = reordered.splice(currIndex, 1);
        reordered.splice(newIndex, 0, movedPlayer);
        return reordered;
      });
    }
  }, []);

  const onSaveReorderHandler = useCallback(() => {
    // Dispatch Redux action to persist the new order
    dispatch(
      experiencesActions.reorderExperiencePlayers({
        experienceId: experience.id,
        playerIds: localPlayers.map(p => p.id),
      }),
    );
  }, [dispatch, experience.id, localPlayers]);

  const onCancelReorderHandler = useCallback(() => {
    // Reset local state to original order
    setLocalPlayers(aggregatedExperiencePlayers);
  }, [aggregatedExperiencePlayers]);

  const onTableRowReorderHandler = useCallback(
    (e: DataTableRowReorderEvent<Player[]>) => {
      onReorderHandler(e.dragIndex, e.dropIndex);
    },
    [onReorderHandler],
  );

  const onSubmitHandler = ({ players }: DeepPartial<ExperienceFormData>) =>
    onSubmit({ device_ids: players?.map(player => player?.id).filter(_ => _) });

  return (
    <EditableDetailsCardDecorator<ExperienceFormData>
      initialValue={{ experience, players: aggregatedExperiencePlayers }}
      validationResolver={yupResolver(updateValidationSchema.players)}
      onSubmit={onSubmitHandler}
    >
      {cardProps => (
        <DetailsCard
          title="Players"
          icon="desktop"
          actions={
            <ExperienceDetailsPlayersCardActionsMenu
              mode={isReordering ? "REORDERING" : cardProps.isEditing ? "EDITING" : "DEFAULT"}
              isDirty={cardProps.isDirty}
              isAggregated={isAggregatedEntity(cardProps.entity.experience)}
              onEdit={cardProps.onEditRow("players")}
              onReset={cardProps.onCancelRow("players")}
              onSubmit={cardProps.handleSubmit(cardProps.onSubmitForm)}
              onSaveReorder={onSaveReorderHandler}
              onCancelReorder={onCancelReorderHandler}
            />
          }
        >
          {/* Display mode: Show DetailsTable */}
          {!cardProps.isEditing && !isAggregatedEntity(cardProps.entity.experience) && (
            <>
              {cardProps.entity.players?.length ? (
                <DetailsTable
                  value={tablePlayers}
                  className="experience-players-table"
                  onRowReorder={onTableRowReorderHandler}
                >
                  {getReorderColumn({ rowReorder: true })}
                  <Column
                    header="Player Name"
                    body={(player: Player, props) => (
                      <>
                        <span className="text-sm text-gray-500">{props.rowIndex + 1}.</span>
                        {` `}
                        <TextLink to={composeDetailsRouteByEntityType(player)}>{player.name}</TextLink>
                      </>
                    )}
                    resizeable={false}
                  />
                  <Column
                    header="Status"
                    body={(player: Player) => getPlayerStatusIndicator(player)}
                    className="w150"
                    resizeable={false}
                  />
                </DetailsTable>
              ) : (
                <DetailsCardRow label="Choose players">
                  <UnassignedLabel text="Experience(s) has no players" />
                </DetailsCardRow>
              )}
            </>
          )}

          {/* Aggregated entity: Show multiple values label */}
          {!cardProps.isEditing && isAggregatedEntity(cardProps.entity.experience) && (
            <DetailsCardRow label="Choose players">
              {isMultipleFieldValuesInAggregatedEntity(cardProps.entity.experience, "device_ids") ? (
                <MultipleValuesLabelWithLink to={composeDetailsRouteByEntityType(aggregatedExperiencePlayers)} />
              ) : (
                <UnassignedLabel text="Experience(s) has no players" />
              )}
            </DetailsCardRow>
          )}

          {/* Edit mode: Show current editing interface */}
          {cardProps.isEditing && (
            <EditableDetailsCardRowDecorator
              field="players"
              input={<ExperiencePlayersControl control={cardProps.control} autoFocus excludeLabel />}
              shouldEditWholeCard
              shouldHideEdit={isAggregatedEntity(cardProps.entity.experience)}
              {...cardProps}
            >
              {rowProps => <EditableDetailsCardRow label="Choose players" editableSettings={rowProps} />}
            </EditableDetailsCardRowDecorator>
          )}
        </DetailsCard>
      )}
    </EditableDetailsCardDecorator>
  );
};
